import { Component, inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef }   from '@angular/material/dialog';
import { ActivatedRoute }            from '@angular/router';

import { AccountAccessData }                                                                   from '../../../api/account-access/account-access';
import { AccountAccessService }                                                                from '../../../api/account-access/account-access.service';
import { ConfirmDialogComponent }                                                              from '../../../shared/components/dialog/cancel-confirm/confirm-dialog.component';
import { GenericDataGridColumnDef, GenericDataGridColumnFilterMode, GenericDataGridComponent } from '../../../shared/components/generic-data-grid/generic-data-grid.component';
import { StringUtils }                                                                         from '../../../shared/utils/string.utils';
import { PageComponent }                                                                       from '../../../shared/components/page/page.component';
import { PageTitleComponent }                                                                  from '../../../shared/components/page/title/title.component';
import { PageDescriptionComponent }                                                            from '../../../shared/components/page/description/description.component';
import { PageContentComponent }                                                                from '../../../shared/components/page/content/content.component';
import { ButtonComponent }                                                                     from '../../../shared/components/buttons/button.component';
import { PinNotificationComponent }                                                            from '../../../shared/components/notification/notification.component';
import { PageTitleType }                                                                       from '../../../shared/components/page/title/title';

@Component({
  selector: 'pin-manage-access',
  templateUrl: './manage-access.component.html',
  imports: [PageComponent, PageTitleComponent, PageDescriptionComponent, PageContentComponent, ButtonComponent, PinNotificationComponent, GenericDataGridComponent]
})
export class ManageAccessComponent implements OnInit {
  protected readonly PageTitleType = PageTitleType;
  accountAccessData: AccountAccessData [];
  userInfo: string;
  agentEmail;
  isLoading = false;
  revokeAccess = false;
  sharedAccess = false;
  agentsFeatureEnabled = false;
  isSso = false;
  productName: string;
  initialSortCol = '';
  columnDefinitions: GenericDataGridColumnDef[] = [];
  displayedColumns = [];
  private route = inject(ActivatedRoute);
  private accountAccessService = inject(AccountAccessService);
  private dialog = inject(MatDialog);
  private dialogRef: MatDialogRef<ConfirmDialogComponent>;
  private defaultColumnDefinitions: GenericDataGridColumnDef[] = [
    {
      columnDef: 'emailAddress',
      header: 'EMAIL ADDRESS',
      cell: row => row.emailAddress,
      cellClass: 'col-top',
      sortable: true,
      filterable: true,
      filterMode: GenericDataGridColumnFilterMode.BY_DISPLAYED_VALUE
    },
    {
      columnDef: 'registeredName',
      header: 'REGISTERED NAME',
      cell: row => row.registeredName ? row.registeredName : 'Unregistered',
      cellClass: 'col-top',
      cellConditionalClass: row => row.registeredName ? '' : 'col-italic',
      sortable: true,
      filterable: true,
      filterMode: GenericDataGridColumnFilterMode.BY_DISPLAYED_VALUE
    },
    {
      columnDef: 'depositorList',
      header: 'DEPOSITOR',
      type: 'unorderedList',
      cell: row => row.accountOwnerList,
      cellClass: 'col-top',
      sortable: false,
      filterable: true,
      filterMode: GenericDataGridColumnFilterMode.BY_DISPLAYED_VALUE
    },
    {
      columnDef: 'accountTitlesList',
      header: 'ACCOUNT TITLE AND TRANSACTION ACCOUNT NUMBER',
      type: 'unorderedList',
      cell: row => row.accountTitlesList,
      cellClass: 'col-top',
      sortable: false,
      filterable: true,
      filterMode: GenericDataGridColumnFilterMode.BY_DISPLAYED_VALUE
    },
    {
      columnDef: 'canRemoveAccess',
      header: '',
      type: 'actionButton',
      linkIcon: 'cancel',
      cell: () => 'Remove Access',
      cellAction: row => this.displayRevokeAccessDialog(row),
      cellClass: 'col-top col-centered ',
      disabled: row => !row.canRemoveAccess,
      sortable: false,
      filterable: false
    }
  ];

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParamMap;
    this.sharedAccess = queryParams.has('sharedAccess') ? queryParams.get('sharedAccess') === 'true' : false;
    if (this.sharedAccess) {
      window.scroll(0, 0);
    }
    this.getSession();
    this.loadAccountAccessData();
  }

  tableDataTransformer(userData: AccountAccessData[]) {
    const result = [];
    let count = 0;
    userData.forEach(({
                        emailAddress,
                        registeredName,
                        manageAccountAccesses,
                        username
                      }, _index) => {
      manageAccountAccesses.forEach(({
                                       accountDetailsList,
                                       accountOwnersMap,
                                       canRemoveAccess
                                     }) => {
        let accountTitlesList = [];
        const accountIdList = [];

        const accountOwnerList = Object.keys(accountOwnersMap)
          .map(key => accountOwnersMap[key].name)
          .sort();

        accountDetailsList.forEach(({
                                      accountId,
                                      accountTitles,
                                      transactionAccountNumber
                                    }) => {
          accountIdList.push(accountId);
          accountTitlesList.push(`${accountTitles[0]} - ${transactionAccountNumber}`);
        });

        accountTitlesList = accountTitlesList.sort();

        const id = count.toString();
        count++;

        result.push({
          emailAddress,
          registeredName,
          accountOwnerList,
          accountTitlesList,
          accountIdList,
          canRemoveAccess,
          id,
          username
        });
      });
    });

    return result;
  }

  displayRevokeAccessDialog(row): void {
    this.sharedAccess = false;
    this.revokeAccess = false;
    this.userInfo = row.registeredName ? row.registeredName : row.emailAddress;

    this.dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '700px',
      disableClose: true,
      panelClass: 'pin-confirm-dialog',
      data: {
        title: 'Remove Access',
        subheading: `Are you sure you want to remove access for ${this.userInfo}?`,
        confirmLabel: 'REMOVE ACCESS'
      }
    });

    this.dialogRef.afterClosed().subscribe(closeType => {
      if (closeType === 'confirm') {
        this.accountAccessService
          .revokeUserAccess(row.emailAddress, row.username, row.accountIdList)
          .subscribe(() => {
            this.loadAccountAccessData();
            this.revokeAccess = true;
            this.sharedAccess = false;
            this.agentEmail = row.emailAddress;
            window.scroll(0, 0);
          });
      }
    });
  }

  loadAccountAccessData() {
    this.isLoading = true;
    this.accountAccessService.getAccountsAccessData().subscribe(
      data => {
        this.accountAccessData = data;
        this.isLoading = false;
      });
  }

  getSession() {
    const userSession = this.route.snapshot.data.userSession;

    this.initialSortCol = 'emailAddress';
    this.columnDefinitions = [...this.defaultColumnDefinitions];
    this.displayedColumns = ['emailAddress', 'registeredName', 'depositorList', 'accountTitlesList', 'canRemoveAccess'];

    this.agentsFeatureEnabled = userSession.agentsFeatureEnabled;
    this.isSso = userSession.sso;
    this.productName = userSession.bankCustomizationData?.productName;
  }

  getProductName(addRegisteredTradeMark: boolean) {
    return this.productName ?
      this.productName :
      StringUtils.format('ICS{0}', (addRegisteredTradeMark ? '\u00AE' : ''));
  }
}
